import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../state/authStore";
import { useTeamStore } from "../state/teamStore";

/* ---------------------------------------------------------------------------
   TeamOnboarding — Clean, focused page for creating your first team
   Route: /onboarding/team/new
   --------------------------------------------------------------------------- */

export default function TeamOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { createTeam } = useTeamStore();

  const defaultName = user?.displayName
    ? `${user.displayName}'s Team`
    : "My Team";

  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && !submitting;

  const handleCreate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const team = await createTeam(name.trim(), description.trim() || undefined);
      navigate(`/teams/${team.id}`);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && canSubmit) {
      void handleCreate();
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Welcome text */}
        <div style={styles.header}>
          <h1 style={styles.heading}>Welcome to StintSense</h1>
          <p style={styles.subtext}>
            Let's set up your team. You can invite teammates and start
            comparing setups as soon as you're ready.
          </p>
        </div>

        {/* Form */}
        <div style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="team-name">
              Team Name
            </label>
            <input
              id="team-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="input"
              placeholder="e.g. Apex Motorsports"
              autoFocus
              maxLength={60}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="team-desc">
              Description{" "}
              <span style={styles.optional}>(optional)</span>
            </label>
            <textarea
              id="team-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input"
              placeholder="What's this team about?"
              rows={3}
              maxLength={200}
            />
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button
            className="btn btn-primary"
            style={{ marginTop: "0.5rem" }}
            onClick={handleCreate}
            disabled={!canSubmit}
          >
            {submitting ? "Creating team…" : "Create team"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Styles
   --------------------------------------------------------------------------- */

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "calc(100vh - 80px)",
    padding: "2rem 1.25rem",
  },

  container: {
    width: "100%",
    maxWidth: 440,
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
  },

  header: {
    textAlign: "left" as const,
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },

  heading: {
    fontFamily: "var(--font-display)",
    fontWeight: 600,
    fontSize: "1.65rem",
    letterSpacing: "-0.03em",
    color: "var(--text-strong)",
    lineHeight: 1.05,
  },

  subtext: {
    fontFamily: "var(--font-body)",
    fontSize: "0.88rem",
    color: "var(--text-secondary)",
    lineHeight: 1.5,
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1.25rem",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
  },

  label: {
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "var(--text-secondary)",
  },

  optional: {
    fontWeight: 400,
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
  },

  error: {
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    color: "var(--flag-red)",
    padding: "0.4rem 0.6rem",
    background: "var(--flag-red-soft)",
    borderRadius: "var(--r-sm)",
  },
};
